import React, { useState } from 'react';
import {
  Button,
  Card,
  Col,
  Row,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  Alert,
  Space,
  Tooltip as AntTooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  ShareAltOutlined,
  TagOutlined,
  TeamOutlined,
  UserAddOutlined,
  TrophyOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { engagementAdminService } from '../../services/engagementAdmin.service';
import { UserDetailsModal } from '../../components/admin/UserDetailsModal';
import dayjs from 'dayjs';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

const { Title, Text } = Typography;

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  OPEN: { label: 'Em andamento', color: 'green', icon: <ClockCircleOutlined /> },
  VALIDATING: { label: 'Validando', color: 'orange', icon: <ClockCircleOutlined /> },
  CLOSED: { label: 'Encerrado', color: 'default', icon: <CheckCircleOutlined /> },
};

const GEO_COLORS = ['#52c41a', '#d9d9d9'];

const buildTop10Columns = (onViewDetails: (userId: number) => void) => [
  {
    title: '#',
    dataIndex: 'rank',
    key: 'rank',
    width: 50,
    render: (rank: number) => <Text strong>{rank}</Text>,
  },
  {
    title: 'Participante',
    key: 'name',
    render: (_: any, record: any) => (
      <Text>
        {record.firstName}
        {record.lastNameMasked ? ` ${record.lastNameMasked}` : ''}
      </Text>
    ),
  },
  {
    title: 'Email',
    dataIndex: 'emailMasked',
    key: 'emailMasked',
    render: (email: string) => <Text type="secondary">{email}</Text>,
  },
  {
    title: 'Pontos',
    dataIndex: 'totalPoints',
    key: 'totalPoints',
    align: 'right' as const,
    render: (points: number) => <Text strong>{points}</Text>,
  },
  {
    title: 'Ação',
    key: 'action',
    width: 70,
    align: 'center' as const,
    render: (_: any, record: any) => (
      <AntTooltip title="Ver detalhes">
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => onViewDetails(record.userId)}
        />
      </AntTooltip>
    ),
  },
];

const top10ReferralsColumns = [
  {
    title: '#',
    dataIndex: 'rank',
    key: 'rank',
    width: 50,
    render: (rank: number) => <Text strong>{rank}</Text>,
  },
  {
    title: 'Participante',
    key: 'name',
    render: (_: any, record: any) => (
      <Text>
        {record.firstName}
        {record.lastNameMasked ? ` ${record.lastNameMasked}` : ''}
      </Text>
    ),
  },
  {
    title: 'Email',
    dataIndex: 'emailMasked',
    key: 'emailMasked',
    render: (email: string) => <Text type="secondary">{email}</Text>,
  },
  {
    title: 'Total de Indicações',
    dataIndex: 'totalReferrals',
    key: 'totalReferrals',
    align: 'right' as const,
    render: (count: number) => <Text strong>{count}</Text>,
  },
];

const buildReferredUsersColumns = (onViewDetails: (userId: number) => void) => [
  {
    title: 'Indicado',
    key: 'name',
    render: (_: any, record: any) => (
      <Text>
        {record.firstName}
        {record.lastNameMasked ? ` ${record.lastNameMasked}` : ''}
      </Text>
    ),
  },
  {
    title: 'Email',
    dataIndex: 'emailMasked',
    key: 'emailMasked',
    render: (email: string) => <Text type="secondary">{email}</Text>,
  },
  {
    title: 'Data de Inclusão',
    dataIndex: 'registeredAt',
    key: 'registeredAt',
    render: (date: string | null) => (date ? dayjs(date).format('DD/MM/YYYY') : '-'),
  },
  {
    title: 'Ação',
    key: 'action',
    width: 70,
    align: 'center' as const,
    render: (_: any, record: any) => (
      <AntTooltip title="Ver detalhes">
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => onViewDetails(record.userId)}
        />
      </AntTooltip>
    ),
  },
];

const deviceFingerprintColumns = [
  {
    title: 'Device Fingerprint',
    dataIndex: 'deviceFingerprint',
    key: 'deviceFingerprint',
    render: (fp: string) => (
      <Text code style={{ fontSize: 12 }}>
        {fp.length > 12 ? `${fp.slice(0, 12)}…` : fp}
      </Text>
    ),
  },
  {
    title: 'Usuários distintos',
    dataIndex: 'userCount',
    key: 'userCount',
    align: 'right' as const,
    render: (count: number) => <Tag color="warning">{count} usuários</Tag>,
  },
];

const fingerprintUsersColumns = [
  {
    title: 'Participante',
    key: 'name',
    render: (_: any, record: any) => (
      <Text>
        {record.firstName}
        {record.lastNameMasked ? ` ${record.lastNameMasked}` : ''}
      </Text>
    ),
  },
  {
    title: 'Email',
    dataIndex: 'emailMasked',
    key: 'emailMasked',
    render: (email: string) => <Text type="secondary">{email}</Text>,
  },
];

export const EngagementCycleReportPage: React.FC = () => {
  const { cycleKey } = useParams<{ cycleKey: string }>();
  const navigate = useNavigate();

  const { data: report, isLoading, isError } = useQuery({
    queryKey: ['admin', 'engagement-cycle-report', cycleKey],
    queryFn: () => engagementAdminService.getCycleReport(cycleKey!),
    enabled: !!cycleKey,
  });

  const [detailsUserId, setDetailsUserId] = useState<number | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const handleViewDetails = (userId: number) => {
    setDetailsUserId(userId);
    setDetailsOpen(true);
  };
  const top10Columns = buildTop10Columns(handleViewDetails);
  const referredUsersColumns = buildReferredUsersColumns(handleViewDetails);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isError || !report) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          message="Ciclo não encontrado"
          description={`O ciclo "${cycleKey}" não existe ou não pôde ser carregado.`}
          action={
            <Button onClick={() => navigate('/admin/engagement/cycles')}>
              Voltar à listagem
            </Button>
          }
        />
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[report.cycle.status] ?? {
    label: report.cycle.status,
    color: 'default',
    icon: null,
  };

  const period = `${dayjs(report.cycle.startsAt).format('DD/MM/YYYY')} → ${dayjs(report.cycle.endsAt).format('DD/MM/YYYY')}`;

  const geoStats = report.geoStats ?? { totalResponses: 0, withGeo: 0, withoutGeo: 0, withGeoPercent: 0, withoutGeoPercent: 0 };
  const geoChartData = [
    { name: `Com geolocalização (${geoStats.withGeoPercent}%)`, value: geoStats.withGeo },
    { name: `Sem geolocalização (${geoStats.withoutGeoPercent}%)`, value: geoStats.withoutGeo },
  ];

  const deviceFingerprintAlerts: any[] = report.deviceFingerprintAlerts ?? [];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <div>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin/engagement/cycles')}
            style={{ marginBottom: 12 }}
          >
            Voltar
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Title level={3} style={{ margin: 0 }}>
              Relatório do Ciclo {report.cycle.cycleKey}
            </Title>
            <Tag color={statusCfg.color} icon={statusCfg.icon}>
              {statusCfg.label}
            </Tag>
          </div>
          <Text type="secondary">{period}</Text>
          <Text type="secondary" style={{ marginLeft: 16 }}>
            Pontuação mínima: {report.cycle.minimumPrizePoints} pts
          </Text>
        </div>

        {/* Indicator cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Bilhetes Gerados"
                value={report.indicators.totalTickets}
                prefix={<TagOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Bilhetes Válidos"
                value={report.indicators.validTickets}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Usuários Acima do Mínimo"
                value={report.indicators.usersAboveMinimum}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Novos Usuários Indicados"
                value={report.indicators.newReferredUsers}
                prefix={<UserAddOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Top 10 table */}
        <Card
          title={
            <Space>
              <TrophyOutlined />
              Top 10 por Pontuação
            </Space>
          }
        >
          <Table
            dataSource={report.top10}
            columns={top10Columns}
            rowKey="rank"
            pagination={false}
            size="small"
            locale={{ emptyText: 'Sem participantes pontuados neste ciclo' }}
          />
        </Card>

        {/* Top 10 referrals table */}
        <Card
          title={
            <Space>
              <ShareAltOutlined />
              Top 10 por Indicações
            </Space>
          }
        >
          <Table
            dataSource={report.top10Referrals}
            columns={top10ReferralsColumns}
            rowKey="rank"
            pagination={false}
            size="small"
            locale={{ emptyText: 'Sem indicações neste ciclo' }}
            expandable={{
              expandedRowRender: (record: any) => (
                <Table
                  dataSource={record.referredUsers}
                  columns={referredUsersColumns}
                  rowKey={(referredUser: any) =>
                    `${referredUser.emailMasked}-${referredUser.registeredAt ?? ''}`
                  }
                  pagination={false}
                  size="small"
                  style={{ margin: 0 }}
                />
              ),
              rowExpandable: (record: any) => record.referredUsers?.length > 0,
            }}
          />
        </Card>

        {/* Device fingerprint alerts */}
        <Card
          title={
            <Space>
              <WarningOutlined style={{ color: '#faad14' }} />
              Dispositivos Compartilhados
            </Space>
          }
          extra={
            deviceFingerprintAlerts.length > 0 ? (
              <Tag color="warning">{deviceFingerprintAlerts.length} alerta{deviceFingerprintAlerts.length > 1 ? 's' : ''}</Tag>
            ) : null
          }
        >
          <Table
            dataSource={deviceFingerprintAlerts}
            columns={deviceFingerprintColumns}
            rowKey="deviceFingerprint"
            pagination={false}
            size="small"
            locale={{ emptyText: 'Nenhum dispositivo compartilhado identificado' }}
            expandable={{
              expandedRowRender: (record: any) => (
                <Table
                  dataSource={record.users}
                  columns={fingerprintUsersColumns}
                  rowKey={(u: any) => u.emailMasked}
                  pagination={false}
                  size="small"
                  style={{ margin: 0 }}
                />
              ),
              rowExpandable: (record: any) => record.users?.length > 0,
            }}
          />
        </Card>

        {/* Geolocation stats */}
        <Card
          title={
            <Space>
              <EnvironmentOutlined />
              Geolocalização nas Respostas
            </Space>
          }
          extra={
            <Text type="secondary">
              {geoStats.totalResponses.toLocaleString('pt-BR')} resposta{geoStats.totalResponses !== 1 ? 's' : ''} no período
            </Text>
          }
        >
          {geoStats.totalResponses === 0 ? (
            <Text type="secondary">Sem respostas registradas neste período.</Text>
          ) : (
            <Row gutter={[24, 16]} align="middle">
              <Col xs={24} md={12}>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={geoChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      dataKey="value"
                    >
                      {geoChartData.map((_, index) => (
                        <Cell key={index} fill={GEO_COLORS[index]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => value.toLocaleString('pt-BR')} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Col>
              <Col xs={24} md={12}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Statistic
                    title="Com geolocalização"
                    value={geoStats.withGeoPercent}
                    suffix="%"
                    valueStyle={{ color: '#52c41a' }}
                    prefix={<EnvironmentOutlined />}
                  />
                  <Text type="secondary">
                    {geoStats.withGeo.toLocaleString('pt-BR')} de {geoStats.totalResponses.toLocaleString('pt-BR')} respostas
                  </Text>
                  <Statistic
                    title="Sem geolocalização"
                    value={geoStats.withoutGeoPercent}
                    suffix="%"
                    valueStyle={{ color: '#8c8c8c' }}
                  />
                  <Text type="secondary">
                    {geoStats.withoutGeo.toLocaleString('pt-BR')} de {geoStats.totalResponses.toLocaleString('pt-BR')} respostas
                  </Text>
                </Space>
              </Col>
            </Row>
          )}
        </Card>
      </Space>

      <UserDetailsModal
        userId={detailsUserId}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
      />
    </div>
  );
};

export default EngagementCycleReportPage;
